import React, { useContext, useState } from 'react';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { ExpeditionDetailV4OutDTO, ExpeditionStatusEnum, FeaturesEnum } from '@api/ringil-api.v2';
import { RowForTagsAndBadges } from '@components/styles';
import COHeadline from '@components/framework/headlines/COHeadline.tsx';
import ArrangedQuoteTable from '@components/ringil3/Features/Expedition/ArrangedQuoteTable.tsx';
import PriceBadge from '@components/ringil3/Badges/PriceBadge.tsx';
import OrderedExpeditionsBadge from '@components/ringil3/Badges/OrderedExpeditionsBadge.tsx';
import TrophySvg from '@components/ringil3/icons/heading/TrophySvg.tsx';
import RequestedManipulationUnitsBadge from '@components/ringil3/Badges/RequestedManipulationUnitsBadge.tsx';
import { Ringil3BadgeRow } from '@components/ringil3/styles.tsx';
import { sumPriceDTOs } from '@utils/priceUtils.ts';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import ConfirmDialog from '@components/RingilComponents/ConfirmDialog.tsx';
import { ApiContext } from '@api/api.tsx';
import { useRequestCarrierSidebar } from '@components/ringil3/Features/Expedition/Sidebars/RequestCarrierSidebar.tsx';
import { getAiCarrierAutoSelectionDto, isLadenFactorEligible } from '@utils/expeditionUtils.ts';
import { SecuredConfig, useSecuredDecoration } from '@components/security/securedDecoration';
import { useSecuredFlag } from '@components/security/useSecuredFlag';
import SecuredArea from '@components/security/SecuredArea';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser.tsx';
import { EXPEDITION_RULE_DESCRIPTION } from '@components/ringil3/Features/Expedition/expeditionSecurityTexts';

interface ComponentProps {
  expedition: ExpeditionDetailV4OutDTO;
  onReload: () => void;
  secured?: SecuredConfig;
}

const TabExpeditionArrangedQuotes: React.FC<ComponentProps> = ({ expedition, onReload, secured }) => {
  const { tr } = useTranslationLgs();
  const { decorate } = useSecuredDecoration(secured);
  const { hasFeature } = useContext(AuthenticatedUserContext);

  const { sendExternalQuote } = useContext(ApiContext);
  const { RequestCarrierSidebar, setVisible: setVisibleRequestCarrierSidebar } = useRequestCarrierSidebar();

  const [visibleSapSubmitDialog, setVisibleSapSubmitDialog] = useState(false);

  const totalPriceDto = sumPriceDTOs(expedition.ladenFactorExpeditions.filter(expedition => expedition.expeditionStatus !== ExpeditionStatusEnum.CANCELED).map(expedition => expedition.priceDTO));
  if (totalPriceDto) {
    totalPriceDto.price = totalPriceDto.companyComputedPrice;
    totalPriceDto.currency = totalPriceDto.companyComputedCurrency;

  }

  const sendExpeditionToSap = () => {
    sendExternalQuote(
      expedition.id,
      () => {
        onReload();
      });
  };

  const manipulationUnitsUsed = expedition.ladenFactorExpeditions
    .filter(expedition => expedition.expeditionStatus !== ExpeditionStatusEnum.CANCELED)
    .reduce((sum, lf) => sum + (lf.manipulationUnits || 0), 0);
  const createNewDistributionFlag = useSecuredFlag('arranged-create-distribution', {
    kind: 'action',
    description: 'Create new distribution button',
    features: [FeaturesEnum.MULTIPLE_DISTRIBUTIONS],
    // Capacity and a running distribution are states of the shipment, not "nothing to show": as `data` they would never reach the AI assistant.
    state: [
      {
        label: 'expeditionStatus ∉ {FINISHED, CANCELED}',
        satisfied: expedition.expeditionStatus !== ExpeditionStatusEnum.FINISHED && expedition.expeditionStatus !== ExpeditionStatusEnum.CANCELED,
        description: EXPEDITION_RULE_DESCRIPTION.finishedOrCancelled,
        byDesign: true,
      },
      {
        label: 'handlingUnitCount !== manipulationUnitsUsed',
        satisfied: expedition.handlingUnitCount !== manipulationUnitsUsed,
        description: "Offered only while some of the shipment's handling units are not yet covered by laden factor shipments",
        byDesign: true,
      },
      {
        label: 'no active distribution',
        satisfied: !expedition.distributions.find(distribution => distribution.isActive),
        description: 'Offered only while no distribution (round of asking carriers) is running on the shipment',
        byDesign: true,
      },
    ],
  });

  return (
    <>{decorate(<>
      <div className="flex flex-col gap-4">
        <div className="flex flex-row justify-between">
          <COHeadline
            variant={'h2'}
            title={tr(`TabExpeditionArrangedQuotes.title`, `Arranged quotes`)}
            icon={<TrophySvg />}
          />
          <div className="flex flex-row gap-2">
            <SecuredArea id="arranged-create-distribution" label="Create new distribution" description="Button that starts a new distribution: a new round of asking carriers for quotes" visible={createNewDistributionFlag}>
              <RingilButton
                mode={'table'}
                onClick={() => setVisibleRequestCarrierSidebar(true)}
                label={tr('TabExpeditionArrangedQuotes.createNewDistribution', 'Create new distribution')}
              />
            </SecuredArea>
            <SecuredArea
              id="arranged-submit-to-sap"
              label="Submit to SAP"
              description="Submit the shipment to SAP"
              visible={[
                {
                  type: 'state',
                  label: 'expeditionStatus !== FINISHED',
                  satisfied: expedition.expeditionStatus !== ExpeditionStatusEnum.FINISHED,
                  description: 'Not offered on a finished shipment',
                  byDesign: true,
                },
              ]}
            >
              <RingilButton
                mode={'outlined-rectangle'}
                onClick={() => setVisibleSapSubmitDialog(true)}
                label={tr('TabExpeditionArrangedQuotes.submitToSap', 'Submit to SAP')}
              />
            </SecuredArea>
          </div>
        </div>
        <RowForTagsAndBadges>
          <Ringil3BadgeRow>
            <PriceBadge priceDto={totalPriceDto} />
            <OrderedExpeditionsBadge
              ladenFactorExpeditions={expedition.ladenFactorExpeditions}
            />
            <RequestedManipulationUnitsBadge manipulationUnits={expedition.handlingUnitCount} />
          </Ringil3BadgeRow>
        </RowForTagsAndBadges>

        <ArrangedQuoteTable expeditions={expedition.ladenFactorExpeditions}></ArrangedQuoteTable>

        <ConfirmDialog
          visible={visibleSapSubmitDialog}
          dialogTitle={tr('ButtonMoreNavigation.doYouWantToSubmitToSapTheExpeditionTitle', 'Do you want to submit to SAP the expedition?')}
          dialogMessage={tr('ButtonMoreNavigation.doYouWantToSubmitToSapTheExpeditionMessage', 'Do you want to submit to SAP the expedition?')}
          onCancel={() => setVisibleSapSubmitDialog(false)}
          onConfirm={() => {
            sendExpeditionToSap();
            setVisibleSapSubmitDialog(false);
          }}
          onAcceptButtonText={tr(`ButtonMoreNavigation.submit`, `Submit`)}
          onCancelButtonText={tr(`ButtonMoreNavigation.close`, `Close`)}
        />

        <RequestCarrierSidebar
          onContinue={() => {
            onReload();
          }}
          handlingUnitCount={expedition.handlingUnitCount - manipulationUnitsUsed}
          ladenFactorEligible={isLadenFactorEligible(expedition, hasFeature([FeaturesEnum.LADEN_FACTOR]))}
          aiCarrierAutoSelectionDto={getAiCarrierAutoSelectionDto(expedition)}
          expedition={expedition}
        />
      </div>
    </>)}</>
  );
};

export default TabExpeditionArrangedQuotes;
